import React, { useEffect, useMemo, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Users, CalendarDays } from "lucide-react";

const WEEKDAY_RO = ["D", "L", "Ma", "Mi", "J", "V", "S"];

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Calendar reutilizabil: cine e în concediu (doar cereri aprobate).
export function TeamLeaveCalendar() {
  const [items, setItems] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [cal, hol] = await Promise.all([
          api.get(`/timeoff/calendar?month=${month}`),
          api.get(`/timeoff/holidays?month=${month}`).catch(() => ({ data: [] })),
        ]);
        setItems(cal.data || []);
        setHolidays(hol.data || []);
      } catch (e) { toast.error(apiErr(e)); }
      finally { setLoading(false); }
    })();
  }, [month]);

  const [y, m] = month.split("-").map(Number);
  const daysInMonth = new Date(y, m, 0).getDate();
  const days = useMemo(() => Array.from({ length: daysInMonth }, (_, i) => i + 1), [daysInMonth]);
  const weekendDay = (d) => {
    const wd = new Date(y, m - 1, d).getDay();
    return wd === 0 || wd === 6;
  };
  const holidayByDay = useMemo(() => {
    const map = {};
    for (const h of holidays) {
      const dd = Number(h.date.slice(8, 10));
      map[dd] = h.name;
    }
    return map;
  }, [holidays]);

  const { rows, dayTotals } = useMemo(() => {
    const byEmp = {};
    const dayTot = {};
    const monthStart = new Date(y, m - 1, 1);
    const monthEnd = new Date(y, m - 1, daysInMonth);
    for (const r of items) {
      const start = new Date(r.start_date + "T00:00:00");
      const end = new Date(r.end_date + "T00:00:00");
      const s = new Date(Math.max(start, monthStart));
      const e = new Date(Math.min(end, monthEnd));
      const empId = r.employee?.id || r.user_id;
      if (!byEmp[empId]) {
        byEmp[empId] = {
          id: empId, name: r.employee?.name || "—",
          job_title: r.employee?.job_title || "", cells: {}, total: 0,
        };
      }
      for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
        const wd = d.getDay();
        if (wd === 0 || wd === 6) continue;
        const day = d.getDate();
        if (!byEmp[empId].cells[day]) {
          byEmp[empId].cells[day] = {
            color: r.type?.color || "#3d68b1",
            type_name: r.type?.name || "Concediu",
          };
          byEmp[empId].total += 1;
          dayTot[day] = (dayTot[day] || 0) + 1;
        }
      }
    }
    const list = Object.values(byEmp).sort((a, b) => a.name.localeCompare(b.name));
    return { rows: list, dayTotals: dayTot };
  }, [items, y, m, daysInMonth]);

  const { todayList, weekList } = useMemo(() => {
    const now = new Date();
    const today = ymd(now);
    const monday = new Date(now);
    const dow = (monday.getDay() + 6) % 7;
    monday.setDate(monday.getDate() - dow);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const inRange = (r, from, to) => r.start_date <= to && r.end_date >= from;
    return {
      todayList: items.filter(r => inRange(r, today, today)),
      weekList: items.filter(r => inRange(r, ymd(monday), ymd(sunday))),
    };
  }, [items]);

  const legend = useMemo(() => {
    const map = {};
    for (const r of items) if (r.type?.name) map[r.type.name] = r.type.color || "#3d68b1";
    return Object.entries(map);
  }, [items]);

  return (
    <div data-testid="team-leave-calendar">
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-ink-muted block mb-1">Luna</label>
          <input type="month" value={month} onChange={e => setMonth(e.target.value)}
            data-testid="teamcal-month" className="border border-neutral-300 px-3 py-2 text-sm bg-white" />
        </div>
        {legend.length > 0 && (
          <div className="ml-auto flex flex-wrap items-center gap-x-4 gap-y-1">
            {legend.map(([name, color]) => (
              <div key={name} className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-sm" style={{ background: color }} />
                <span className="text-[12px] text-ink-muted">{name}</span>
              </div>
            ))}
            {holidays.length > 0 && (
              <div className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-sm" style={{ background: "#b91c1c" }} />
                <span className="text-[12px] text-ink-muted">Sărbătoare legală</span>
              </div>
            )}
          </div>
        )}
        {legend.length === 0 && holidays.length > 0 && (
          <div className="ml-auto flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: "#b91c1c" }} />
            <span className="text-[12px] text-ink-muted">Sărbătoare legală</span>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-ink-muted">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Se încarcă...
        </div>
      ) : rows.length === 0 && holidays.length === 0 ? (
        <div className="text-center py-14 border border-dashed border-neutral-200 rounded-xl text-ink-muted" data-testid="teamcal-empty">
          Nimeni nu are concediu aprobat în luna selectată.
        </div>
      ) : (
        <div className="bg-white border border-neutral-200 overflow-x-auto" data-testid="teamcal-grid">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-neutral-50">
                <th className="sticky left-0 z-10 bg-neutral-50 text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-ink-muted border-b border-neutral-200 min-w-[180px]">
                  Angajat
                </th>
                {days.map(d => (
                  <th key={d} title={holidayByDay[d] || ""}
                    className={`px-1 py-1.5 text-center font-mono text-[11px] border-b border-neutral-200 w-9 ${holidayByDay[d] ? "bg-red-50 text-red-700 font-semibold" : weekendDay(d) ? "bg-neutral-100 text-ink-muted" : "text-ink"}`}>
                    <div>{d}</div>
                    <div className={`text-[9px] ${holidayByDay[d] ? "text-red-500" : "text-ink-muted"}`}>{WEEKDAY_RO[new Date(y, m - 1, d).getDay()]}</div>
                  </th>
                ))}
                <th className="px-3 py-2 text-right font-mono text-[10px] uppercase tracking-widest text-brand border-b border-neutral-200 bg-neutral-50">
                  Zile
                </th>
              </tr>
            </thead>
            <tbody>
              {holidays.length > 0 && (
                <tr data-testid="teamcal-holiday-row" className="bg-red-50/40">
                  <td className="sticky left-0 z-10 bg-red-50/40 px-3 py-2 border-b border-neutral-100">
                    <div className="font-medium text-red-700 flex items-center gap-1.5">
                      <span className="h-2.5 w-2.5 rounded-sm bg-red-600" />Sărbători legale
                    </div>
                    <div className="text-[11px] text-ink-muted">Zile libere · nu se scad din concediu</div>
                  </td>
                  {days.map(d => (
                    <td key={d} title={holidayByDay[d] || ""}
                      className={`px-1 py-2 text-center border-b border-neutral-100 ${holidayByDay[d] ? "bg-red-50" : weekendDay(d) ? "bg-neutral-50/60" : ""}`}>
                      {holidayByDay[d] ? (
                        <span className="inline-block w-5 h-5 rounded-sm mx-auto bg-red-600" />
                      ) : (
                        <span className="text-neutral-300">·</span>
                      )}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right font-display font-bold text-red-600 border-b border-neutral-100 tabular-nums">
                    {Object.keys(holidayByDay).length}
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} data-testid={`teamcal-emp-${r.id}`} className="hover:bg-ink-soft/40">
                  <td className="sticky left-0 z-10 bg-white px-3 py-2 border-b border-neutral-100">
                    <div className="font-medium text-ink truncate">{r.name}</div>
                    <div className="text-[11px] text-ink-muted truncate">{r.job_title}</div>
                  </td>
                  {days.map(d => {
                    const cell = r.cells[d];
                    return (
                      <td key={d} title={cell ? `${r.name} — ${cell.type_name}` : (holidayByDay[d] || "")}
                        className={`px-1 py-2 text-center border-b border-neutral-100 ${holidayByDay[d] ? "bg-red-50/60" : weekendDay(d) ? "bg-neutral-50/60" : ""}`}>
                        {cell ? (
                          <span className="inline-block w-5 h-5 rounded-sm mx-auto" style={{ background: cell.color }} />
                        ) : (
                          <span className="text-neutral-300">·</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 text-right font-display font-bold text-brand border-b border-neutral-100 tabular-nums">
                    {r.total}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-neutral-50">
                <td className="sticky left-0 z-10 bg-neutral-50 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-ink-muted">
                  Total / zi
                </td>
                {days.map(d => (
                  <td key={d} className={`px-1 py-2 text-center font-mono text-[11px] tabular-nums ${holidayByDay[d] ? "bg-red-50" : weekendDay(d) ? "bg-neutral-100" : ""} ${dayTotals[d] ? "text-ink font-semibold" : "text-neutral-300"}`}>
                    {dayTotals[d] || "·"}
                  </td>
                ))}
                <td className="px-3 py-2" />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        <LeavePanel title="Azi în concediu" list={todayList} testid="teamcal-today" />
        <LeavePanel title="Săptămâna aceasta" list={weekList} testid="teamcal-week" />
      </div>
    </div>
  );
}

function LeavePanel({ title, list, testid }) {
  return (
    <div className="bg-white border border-neutral-200 p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Users className="h-4 w-4 text-brand" />
        <h3 className="font-display font-semibold text-ink">{title}</h3>
        <span className="ml-auto text-xs font-mono text-ink-muted">{list.length}</span>
      </div>
      {list.length === 0 ? (
        <div className="text-sm text-ink-muted italic">Nimeni în concediu.</div>
      ) : (
        <div className="space-y-2">
          {list.map((r) => (
            <div key={r.id} className="flex items-center gap-3">
              <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: r.type?.color || "#3d68b1" }} />
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{r.employee?.name}</div>
                <div className="text-[11px] text-ink-muted truncate">{r.type?.name} · până {r.end_date}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default TeamLeaveCalendar;
